"use client";

import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { usePathname, useRouter } from "@ala/core/navigation";

import { currentConfig } from "../constants";
import { resolveRelativePath } from "./resolve-relative-path";
import { transitionRouterContainerClassName } from "./transition-router-container";
import { type Stage, TransitionRouterContextProvider, type TransitionRouterNavigateMethod } from "./transition-router-context";

interface TransitionRouterProps {
  children?: ReactNode;
}

const transitionRouterContainerSelector = `main.${transitionRouterContainerClassName}`;
const transitionRouterAttribute = "data-stage";

export const TransitionRouter = ({ children }: TransitionRouterProps) => {
  const router = useRouter();
  const pathname = usePathname();

  const [stage, setStage] = useState<Stage>("none");

  const checkForReducedMotion = useCallback(() => {
    const query = "(prefers-reduced-motion: reduce)";

    return window.matchMedia(query).matches;
  }, []);

  const waitForContainerAnimation = useCallback(
    (container: Element, animationName: string, setupFn: () => void) =>
      new Promise<void>((resolve) => {
        const onAnimationEnd = () => {
          const animations = container.getAnimations();
          if (animations.length === 0 || (animations[0] as CSSAnimation).animationName !== animationName) {
            return;
          }

          container.removeEventListener("animationend", onAnimationEnd);
          resolve();
        };

        container.addEventListener("animationend", onAnimationEnd);

        setupFn();
      }),
    [],
  );

  const animateExit = useCallback(async () => {
    if (checkForReducedMotion()) {
      return;
    }

    const container = document.querySelector(transitionRouterContainerSelector);

    if (!container) {
      return;
    }

    await waitForContainerAnimation(container, "fade-out", () => {
      setStage("leaving");
      container.setAttribute(transitionRouterAttribute, "leaving");
    });
  }, [checkForReducedMotion, waitForContainerAnimation]);

  const animateEnter = useCallback(async () => {
    if (checkForReducedMotion()) {
      return;
    }

    const container = document.querySelector(transitionRouterContainerSelector);

    if (!container) {
      setStage("none");
      return;
    }

    await waitForContainerAnimation(container, "fade-in", () => {
      setStage("entering");
      container.setAttribute(transitionRouterAttribute, "entering");
    });

    setStage("none");
    container.removeAttribute(transitionRouterAttribute);
  }, [checkForReducedMotion, waitForContainerAnimation]);

  const navigate = useCallback(
    async (navigationFn: () => void, animate: boolean) => {
      if (!animate) {
        navigationFn();
        return;
      }

      if (stage !== "leaving") {
        await animateExit();
      }
      navigationFn();
    },
    [animateExit, stage],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: trigger when Next navigation is completed
  useEffect(() => {
    const currentStage = document.querySelector(transitionRouterContainerSelector)?.getAttribute(transitionRouterAttribute);

    if (currentStage === "leaving") {
      animateEnter();
    } else if (!currentStage) {
      setStage("none");
    }
  }, [pathname]);

  useEffect(() => {
    const container = document.querySelector(transitionRouterContainerSelector);
    const currentStage = container?.getAttribute(transitionRouterAttribute);

    if (currentStage) {
      container?.removeAttribute(transitionRouterAttribute);
      setStage("none");
    }
  }, []);

  useEffect(() => {
    const onPageShow = () => {
      const container = document.querySelector(transitionRouterContainerSelector);
      if (!container) {
        return;
      }

      const animations = container.getAnimations();
      animations.forEach((a) => a.cancel());
    };

    window.addEventListener("pageshow", onPageShow);

    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const push: TransitionRouterNavigateMethod = useCallback(
    (href, options, transitionOptions) => {
      const isSamePage = isLinkToSamePage(href);

      navigate(() => router.push(resolveRelativePath(href, currentConfig), options), (transitionOptions?.animate ?? true) && !isSamePage);
    },
    [navigate, router],
  );

  const replace: TransitionRouterNavigateMethod = useCallback(
    (href, options, transitionOptions) => {
      const isSamePage = isLinkToSamePage(href);

      navigate(
        () => router.replace(resolveRelativePath(href, currentConfig), options),
        (transitionOptions?.animate ?? true) && !isSamePage,
      );
    },
    [navigate, router],
  );

  const contextValue = useMemo(() => ({ push, replace, stage }), [push, replace, stage]);

  return <TransitionRouterContextProvider value={contextValue}>{children}</TransitionRouterContextProvider>;
};

const isLinkToSamePage = (href: string) => {
  if (href.startsWith("/")) {
    return window.location.pathname === href;
  }

  try {
    const url = new URL(href);

    return window.location.protocol === url.protocol && window.location.host === url.host && window.location.pathname === url.pathname;
  } catch (_e) {
    return false;
  }
};
